import React from "react"
import { loadInitialStateFromFirestore } from "../middleware/firestorePersistence"
import { getAuth } from "firebase/auth"

// Store reference to avoid circular dependency
// This gets set when initializeStoreHydration is called from App.jsx
let storeRef = null

const getStore = () => {
  if (!storeRef) {
    throw new Error("Store not initialized. Call initializeStoreHydration(store) first.")
  }
  return storeRef
}

/**
 * Hydrates Redux store with initial state from Firestore
 * This function should be called when the app starts and when user authentication changes
 */

let isHydrated = false
let currentUserId = null

/**
 * Initialize the store hydration process
 * Sets up authentication listeners and handles initial state loading
 * @param {Object} store - Redux store instance
 */
export const initializeStoreHydration = (store) => {
  // Set the store reference
  storeRef = store
  
  const auth = getAuth()

  // Listen for authentication state changes
  auth.onAuthStateChanged(async (user) => {
    const newUserId = user?.uid || null

    // If user changed or logged in for first time, hydrate store
    if (newUserId !== currentUserId) {
      currentUserId = newUserId

      if (newUserId) {
        await hydrateStoreFromFirestore(newUserId)
      } else {
        // User logged out, clear store data
        await clearStoreData()
      }
    }
  })
}

/**
 * Hydrates the Redux store with data from Firestore for a specific user
 * @param {string} userId - The authenticated user's ID
 */
export const hydrateStoreFromFirestore = async (userId) => {
  if (!userId) {
    return
  }

  try {

    // Load initial state from Firestore
    const initialState = await loadInitialStateFromFirestore(userId)

    // Dispatch hydration actions for each slice that has data
    Object.keys(initialState).forEach((sliceName) => {
      const sliceData = initialState[sliceName]
      if (sliceData && Object.keys(sliceData).length > 0) {
        getStore().dispatch({
          type: `${sliceName}/hydrateFromFirestore`,
          payload: sliceData,
        })
      }
    })

    isHydrated = true

    // Dispatch a global hydration complete event
    getStore().dispatch({
      type: "app/hydrationComplete",
      payload: { userId, timestamp: new Date().toISOString() },
    })
  } catch (error) {

    // Dispatch hydration error event
    getStore().dispatch({
      type: "app/hydrationError",
      payload: {
        error: error.message,
        userId,
        timestamp: new Date().toISOString(),
      },
    })
  }
}

/**
 * Clears store data when user logs out
 */
export const clearStoreData = async () => {
  try {

    // Dispatch clear actions for each slice
    const slicesToClear = [
      "budget",
      "userPreferences",
      "chores",
      "expenses",
      "categories",
    ]

    slicesToClear.forEach((sliceName) => {
      getStore().dispatch({
        type: `${sliceName}/hydrateFromFirestore`,
        payload: {},
      })
    })

    isHydrated = false
    currentUserId = null

    // Dispatch logout complete event
    getStore().dispatch({
      type: "app/logoutComplete",
      payload: { timestamp: new Date().toISOString() },
    })

  } catch (error) {
  }
}

/**
 * Manually triggers store hydration (useful for refresh scenarios)
 * @param {string} userId - Optional user ID, will use current auth user if not provided
 */
export const manualHydrateStore = async (userId = null) => {
  const auth = getAuth()
  const targetUserId = userId || auth.currentUser?.uid

  if (targetUserId) {
    await hydrateStoreFromFirestore(targetUserId)
  } else {
  }
}

/**
 * Returns the current hydration status
 */
export const getHydrationStatus = () => ({
  isHydrated,
  currentUserId,
  timestamp: new Date().toISOString(),
})

/**
 * Waits for store hydration to complete
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 10000)
 * @returns {Promise<boolean>} - Resolves to true if hydrated, false if timeout
 */
export const waitForHydration = (timeout = 10000) => {
  return new Promise((resolve) => {
    if (isHydrated) {
      resolve(true)
      return
    }

    let timeoutId

    // Listen for hydration complete
    const unsubscribe = getStore().subscribe(() => {
      const state = getStore().getState()

      // Check if any slice is hydrated (you can customize this logic)
      const isAnySliceHydrated = Object.values(state).some(
        (slice) =>
          slice && typeof slice === "object" && slice._hydrated === true
      )

      if (isAnySliceHydrated) {
        isHydrated = true
        clearTimeout(timeoutId)
        unsubscribe()
        resolve(true)
      }
    })

    // Set timeout
    timeoutId = setTimeout(() => {
      unsubscribe()
      resolve(false)
    }, timeout)
  })
}

/**
 * Selectively hydrates specific slices
 * @param {string[]} sliceNames - Array of slice names to hydrate
 * @param {string} userId - User ID (optional, uses current auth user)
 */
export const hydrateSpecificSlices = async (sliceNames, userId = null) => {
  const auth = getAuth()
  const targetUserId = userId || auth.currentUser?.uid

  if (!targetUserId) {
    return
  }

  try {

    // Load initial state from Firestore
    const initialState = await loadInitialStateFromFirestore(targetUserId)

    // Dispatch hydration actions only for specified slices
    sliceNames.forEach((sliceName) => {
      const sliceData = initialState[sliceName]
      if (sliceData && Object.keys(sliceData).length > 0) {
        getStore().dispatch({
          type: `${sliceName}/hydrateFromFirestore`,
          payload: sliceData,
        })
      }
    })

  } catch (error) {
  }
}

/**
 * React hook for hydration status
 * Can be used in components to show loading states during hydration
 */
export const useHydrationStatus = () => {
  const [hydrationStatus, setHydrationStatus] =
    React.useState(getHydrationStatus)

  React.useEffect(() => {
    const unsubscribe = getStore().subscribe(() => {
      setHydrationStatus(getHydrationStatus())
    })

    return unsubscribe
  }, [])

  return hydrationStatus
}

// Export for cleanup on app unmount
export const cleanupHydration = () => {
  isHydrated = false
  currentUserId = null
}
